import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './InstructorToolsPage.css';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';

const InstructorToolsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [adminCriteria, setAdminCriteria] = useState(null);
  const [isTestVideoSubmitted, setIsTestVideoSubmitted] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [latestStatus, setLatestStatus] = useState(null);
  const [adminComment, setAdminComment] = useState('');

  const location = useLocation();
  const statusFromNotification = location.state?.status || null;
  const adminCommentFromNotification = location.state?.adminComment || null;

  // Fetch admin video criteria on page load
  useEffect(() => {
    axios.get(api.getAdminCriteria)
      .then((response) => {
        setAdminCriteria(response.data);
      })
      .catch((error) => {
        console.error('Error fetching admin criteria:', error);
      });
  }, []);

  // Fetch latest test video submission status
  useEffect(() => {
    if (!statusFromNotification) {
      axios.get(api.getMyTestVideo, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (res.data.submission) {
            setLatestStatus(res.data.submission.status);
            setAdminComment(res.data.submission.adminComment || '');
          }
        })
        .catch((err) => console.error('Error fetching latest test video', err));
    } else {
      setLatestStatus(statusFromNotification);
      setAdminComment(adminCommentFromNotification || '');
    }
  }, [statusFromNotification, adminCommentFromNotification, token]);

  // Handle video upload
  const handleTestVideoSubmit = async () => {
    if (!videoFile) {
      alert('Please upload a video file before submitting!');
      return;
    }

    const formData = new FormData();
    formData.append('testVideo', videoFile);

    try {
      const response = await axios.post(api.submitTestVideo, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        // After video is successfully uploaded, set the status to 'pending'
        setLatestStatus('pending');
        setIsTestVideoSubmitted(true);
        setVideoUrl(api.getVideoFileUrl(response.data.videoPath)); // Use the API function
      }
    } catch (error) {
      console.error('Error submitting test video:', error);
      alert('Error submitting the video. Please try again!');
    }
  };

  const handleGetStarted = () => {
    navigate('/my-courses');
  };

  if (!adminCriteria) {
    return <div>Loading criteria...</div>;
  }

  return (
    <div className="tools-page">
      {/* Render the criteria from admin */}
      <div className="intro">
        <div dangerouslySetInnerHTML={{ __html: adminCriteria.text }} />
      </div>

      {/* ✅ Status Approved */}
      {latestStatus === 'approved' && (
        <div className="test-video-success">
          <h2>Your test video is <span style={{ color: 'green' }}>Approved ✅</span></h2>

          {/* ✅ Admin Comment immediately after approval */}
          {adminComment && (
            <div className="admin-comment-section">
              <h3
                className="admin-comment-approved"
                style={{
                  margin: '10px',
                  color: 'green',
                  maxWidth: '300px',
                  height: '100px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  padding: '5px',
                  border: '1px solid #ccc',
                }}
              >
                <span style={{ color: 'green' }}>Note:</span>
                <strong className="aa"> {adminComment}</strong>
              </h3>

            </div>
          )}

          <p>You’re ready to proceed with your course recording!</p>
          <button onClick={handleGetStarted}>Get Started</button>
        </div>
      )}

      {/* ✅ Status Rejected */}
      {latestStatus === 'rejected' && (
        <div className="test-video-rejected">
          <h2>Your test video is <span style={{ color: 'Maroon' }}>Rejected ❌</span></h2>
          <p>&quot;Your test video didn&apos;t meet the required criteria. 
                  Please review the feedback and make the necessary adjustments before resubmitting. 
                  We appreciate your effort and look forward to your revised video!&quot;</p>

          {/* ✅ Admin Comment immediately after rejection */}
          {adminComment && (
            <div className="admin-comment-section">
              <h3
                className="admin-comment-rejected"
                style={{
                  margin: '10px',
                  color: 'red',
                  maxWidth: '300px',
                  height: '100px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  wordWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  padding: '5px',
                  border: '1px solid #ccc',
                }}
              >
                <span style={{ color: 'red' }}>Note:</span>
                <strong className="aa"> {adminComment}</strong>
              </h3>

              
            </div>
          )}

          <p>Please correct your setup and submit a new test video below.</p>
        </div>
      )}

      {/* ✅ Status Pending */}
      {latestStatus === 'pending' && (
        <div className="test-video-pending">
          <h2>Your test video is <span style={{ color: 'DarkSlateGrey' }}>Pending ⏳</span></h2>

          {/* ✅ Admin Comment for pending status */}
          {adminComment && (
            <div className="admin-comment-section">
              <h3 className='admin-comment'>Admin Comment: <strong>{adminComment}</strong></h3>
            </div>
          )}

          <p>Your video is under review. Please wait for the admin’s response.</p>
        </div>
      )}

      {/* ✅ Video Submission Form only if not approved */}
      {(latestStatus !== 'approved' && latestStatus !== 'pending' && !isTestVideoSubmitted) && (
        <div className="test-video-form">
          <h2>Submit Your Test Video</h2>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setVideoFile(e.target.files[0])}
          />
          <button onClick={handleTestVideoSubmit}>Submit Test Video</button>
        </div>
      )}

      {/* ✅ Display Uploaded Video */}
      {videoUrl && (
        <div className="uploaded-video">
          <h3>Uploaded Test Video:</h3>
          <video controls>
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
};

export default InstructorToolsPage;
